import { motion } from 'framer-motion'
import { ArrowLeft, User, Save, Loader2, Building, Shield, Mail, Phone, MapPin } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { useEditarUsuario } from '@/hooks/use-editar-usuario'
import type { TipoPermissao } from '@/types'

const PERMISSOES = [
  { value: 'admin', label: 'Administrador', description: 'Acesso total ao sistema' },
  { value: 'gestor', label: 'Gestor', description: 'Gerencia igreja e membros' },
  { value: 'lider', label: 'Líder', description: 'Lidera ministérios e eventos' },
  { value: 'membro', label: 'Membro', description: 'Acesso básico' }
]

export function UsuarioEditarPage() {
  const {
    formState,
    errors,
    isLoading,
    isSubmitting,
    igrejas,
    handleInputChange,
    handleSubmit,
    handleCancel
  } = useEditarUsuario()

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Carregando dados do usuário...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/70">
            <User className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Editar Usuário</h1>
            <p className="text-muted-foreground">Atualize as informações do usuário</p>
          </div>
        </div>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Informações Pessoais
              </CardTitle>
              <CardDescription>
                Dados básicos do usuário
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dados Pessoais */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome Completo *</Label>
                    <Input
                      id="nome"
                      value={formState.nome}
                      onChange={(e) => handleInputChange('nome', e.target.value)}
                      placeholder="Digite o nome completo"
                    />
                    {errors.nome && (
                      <p className="text-sm text-red-500">{errors.nome}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={formState.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="usuario@exemplo.com"
                        className="pl-10"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-sm text-red-500">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="telefone">Telefone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="telefone"
                        value={formState.telefone}
                        onChange={(e) => handleInputChange('telefone', e.target.value)}
                        placeholder="(11) 99999-9999"
                        className="pl-10"
                      />
                    </div>
                    {errors.telefone && (
                      <p className="text-sm text-red-500">{errors.telefone}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="endereco">Endereço</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="endereco"
                        value={formState.endereco}
                        onChange={(e) => handleInputChange('endereco', e.target.value)}
                        placeholder="Endereço completo"
                        className="pl-10"
                      />
                    </div>
                    {errors.endereco && (
                      <p className="text-sm text-red-500">{errors.endereco}</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Igreja e Permissões
              </CardTitle>
              <CardDescription>
                Configurações de acesso e vinculação
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="igreja_id">Igreja *</Label>
                  <Select
                    value={formState.igreja_id}
                    onValueChange={(value) => handleInputChange('igreja_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma igreja" />
                    </SelectTrigger>
                    <SelectContent>
                      {igrejas.map((igreja) => (
                        <SelectItem key={igreja.id} value={igreja.id}>
                          {igreja.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.igreja_id && (
                    <p className="text-sm text-red-500">{errors.igreja_id}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="permissao">Permissão *</Label>
                  <Select
                    value={formState.permissao || ''}
                    onValueChange={(value) => handleInputChange('permissao', value as TipoPermissao)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma permissão" />
                    </SelectTrigger>
                    <SelectContent>
                      {PERMISSOES.map((permissao) => (
                        <SelectItem key={permissao.value} value={permissao.value}>
                          <div className="flex items-center gap-2">
                            <span>{permissao.label}</span>
                            <Badge variant="secondary" className="text-xs">
                              {permissao.description}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.permissao && (
                    <p className="text-sm text-red-500">{errors.permissao}</p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Super Administrador
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Concede acesso total ao sistema, incluindo gerenciamento de outras igrejas
                    </p>
                  </div>
                  <Switch
                    checked={formState.is_admin}
                    onCheckedChange={(checked) => handleInputChange('is_admin', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Status do Usuário</Label>
                    <p className="text-sm text-muted-foreground">
                      Usuários inativos não podem acessar o sistema
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={formState.ativo ? "default" : "secondary"}>
                      {formState.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                    <Switch
                      checked={formState.ativo}
                      onCheckedChange={(checked) => handleInputChange('ativo', checked)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}